// ============ GET TOTAL SPENT USDT ============
  // This helper must be inside the useDolphin hook to access dolphinContract and formatUSDT
 
import { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { CONTRACTS } from '../config';

// Minimal ABI for DolphinExtended (only special address functions)
export const DOLPHIN_EXTENDED_ABI = [
  "function isSpecialAddress(address) view returns (bool)",
  "function getSpecialAddresses() view returns (address[])"
];


// DEBUG: Fetch and log downlines for a specific user
export async function debugFetchDownlinesForUser(dolphinReferralContract, userAddress) {
  if (!dolphinReferralContract || !userAddress) {
    console.error('[debugFetchDownlinesForUser] Missing contract or user address');
    return;
  }
  try {
    const byRank = await dolphinReferralContract.getDownlinesByRank(userAddress, 5);
    const direct = await dolphinReferralContract.getDownlines(userAddress);
    console.log('[DEBUG] getDownlinesByRank for', userAddress, ':', byRank);
    console.log('[DEBUG] getDownlines for', userAddress, ':', direct);
    return { byRank, direct };
  } catch (err) {
    console.error('[debugFetchDownlinesForUser] Error:', err);
    return null;
  }
}
// Minimal ABI for DolphinPayments contract (expand as needed)
export const DOLPHIN_PAYMENTS_ABI = [
  "function payReferral(address,uint256)",
  "function setDolphinReferral(address)",
  "function setUsdtToken(address)",
  "function setMiningDistribution(address[],uint256[])",
  "function setFixedDistribution(address[],uint256[])",
  "function distributeMiningPayment(address,uint256)",
  "function distributeToFixedAddresses(uint256)",
  "function validateRecipient(address) view returns (bool)",
  "function miningRecipients(uint256) view returns (address)",
  "function miningPercents(uint256) view returns (uint256)",
  "function levelFixedAddresses(uint256) view returns (address)",
  "function levelFixedPercents(uint256) view returns (uint256)"
];

// Minimal ABI for DolphinVesting contract (expand as needed)
export const DOLPHIN_VESTING_ABI = [
  "function getClaimableAmount(address) view returns (uint256)",
  "function claimTokens()",
  "function distributeDolpRewards(address,uint256,uint256)",
  "function getClaimableDolp(address) view returns (uint256)"
];
// Minimal ABI for DolphinReferral contract (expand as needed)
export const DOLPHIN_REFERRAL_ABI = [
  "function getDownlinesByRank(address,uint256) view returns (address[][])",
  "function getDownlines(address) view returns (address[])",
  "function userReferralEarnings(address) view returns (uint256)",
  "function addressToInviteCode(address) view returns (bytes32)",
  "function referrerOf(address) view returns (address)",
  "function isUser(address) view returns (bool)"
];

// Only one export for useDolphin should exist. The rest of the file continues as normal.

// Helper to get explorer URL for a tx hash or address (Polygon Mainnet)
export function getExplorerUrl(hashOrAddress) {
  if (!hashOrAddress) return '';
  if (hashOrAddress.startsWith('0x') && hashOrAddress.length === 66) {
    // Transaction hash
    return `https://polygonscan.com/tx/${hashOrAddress}`;
  }
  if (hashOrAddress.startsWith('0x') && hashOrAddress.length === 42) {
    // Address
    return `https://polygonscan.com/address/${hashOrAddress}`;
  }
  return '';
}

// History event types (no pull/manual payment logic remains)
export const HISTORY_TYPES = {
  LEVEL_PURCHASE: 'LEVEL_PURCHASE',
  MINING: 'MINING',
  TOKEN_CLAIM: 'TOKEN_CLAIM',
  REFERRAL_EARNINGS: 'REFERRAL_EARNINGS',
  REFERRAL_SET: 'REFERRAL_SET',
  INVITE_GENERATED: 'INVITE_GENERATED',
  NEW_DOWNLINE: 'NEW_DOWNLINE',
  FREE_LEVELS_GRANTED: 'FREE_LEVELS_GRANTED',
  USER_MIGRATED: 'USER_MIGRATED'
};

// Complete ABI with all functions (no pull/manual payment functions)
export const DOLPHIN_ABI = [
  // Write functions
  "function buyLevel(uint256 level)",
  "function buyLevels(uint256[] levels)",
  "function mineTokens(uint256 usdtAmount)",
  "function claimTokens()",
  "function setReferrer(bytes32 inviteCode)",
  "function generateInviteCode() returns (bytes32)",
  "function migrateUser()",
  // Referral functions
  "function inviteCodeToAddress(bytes32) view returns (address)",
  "function addressToInviteCode(address) view returns (bytes32)",
  "function referrerOf(address) view returns (address)",
  "function getDownlinesByRank(address) view returns (address[][])",
  "function userReferralEarnings(address) view returns (uint256)",
  "function getUserInfo(address) view returns (bool, bool, address, bytes32, uint256[], bool, uint256, uint256, uint256)",
  // View functions
  "function getLevelPriceUSDT(uint256 level) view returns (uint256)",
  "function hasLevel(address user, uint256 level) view returns (bool)",
  "function canMine(address user) view returns (bool)",
  "function isSpecialAddress(address) view returns (bool)",
  "function getSpecialAddresses() view returns (address[])",
  "function getClaimableAmount(address user) view returns (uint256)",
  "function getTotalSpentUSDT(address user) view returns (uint256)",
  // Global stats
  "function minedTokens() view returns (uint256)",
  "function getCurrentPriceUSDT() view returns (uint256)",
  "function miningEnded() view returns (bool)",
  "function miningEnabled() view returns (bool)",
  "function paused() view returns (bool)",
  "function emergencyPaused() view returns (bool)",
  "function migrationEnabled() view returns (bool)",
  // Constants
  "function TOTAL_SUPPLY() view returns (uint256)",
  "function MAX_LEVEL_PRICE_USDT() view returns (uint256)",
  "function CONTRACT_VERSION() view returns (uint256)",
  // Vesting
  "function vestingInfo(address user) view returns (tuple(uint256 totalVested, uint256 totalClaimed, uint256 lastClaimTimestamp, uint256 vestedAtBlock))",

  "function analytics() view returns (tuple(uint256 totalUsers, uint256 totalLevelPurchases, uint256 totalUSDTMiningVolume, uint256 totalReferralPayouts, uint256 totalFixedPayouts, uint256 totalMiningPayouts, uint256 totalRevenue, uint256 lastUpdated))",
  // Events
  "event ReferrerSet(address indexed user, address indexed referrer, bytes32 inviteCode)",
  "event InviteCodeGenerated(address indexed user, bytes32 code)",
  "event LevelPurchased(address indexed user, uint256 level, uint256 amountUSDT)",
  "event MLMPayout(address indexed from, address indexed to, uint256 level, uint256 amountUSDT)",
  "event FixedPayout(address indexed from, address indexed to, uint256 share, uint256 amountUSDT)",
  "event MiningPayout(address indexed from, address indexed to, uint256 share, uint256 amountUSDT)",
  "event TokensMined(address indexed miner, uint256 tokenAmount, uint256 usdtAmount)",
  "event NewDownline(address indexed user, address indexed downline, uint256 level, uint256 timestamp)",
  "event TokensClaimed(address indexed user, uint256 amount)",
  "event FreeLevelsGranted(address indexed user, uint256 levelCount, uint256 timestamp)"
];

export const USDT_ABI = [
  "function balanceOf(address account) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)"
];


export const useDolphin = () => {
  // All state and refs must be declared before any function uses them
  const { account, signer, provider } = useWallet();
  const [dolphinContract, setDolphinContract] = useState(null);
  const [dolphinExtendedContract, setDolphinExtendedContract] = useState(null);
  const [usdtContract, setUsdtContract] = useState(null);
  const [inviteCode, setInviteCode] = useState(null);
  const [dolphinPaymentsContract, setDolphinPaymentsContract] = useState(null);
  const [dolphinVestingContract, setDolphinVestingContract] = useState(null);
  const [dolphinReferralContract, setDolphinReferralContract] = useState(null);
  const [usdtDecimals, setUsdtDecimals] = useState(6);
  const [pendingReferral, setPendingReferral] = useState(null);
  const [referralStats, setReferralStats] = useState({
    totalEarned: '0',
    downlines: [],
    downlineCount: 0,
    canEarn: false,
    hasReferrer: false,
    referrer: null,
    inviteCode: null,
    downlinesByRank: [[], [], [], [], []],
  });
  const [downlineTree, setDownlineTree] = useState({
    loading: false,
    error: null,
    data: null,
    lastUpdated: 0
  });
  const [stats, setStats] = useState({
    startPrice: '0',
    totalSupply: '0',
    minedTokens: '0',
    currentPrice: '0',
    miningEnded: false,
    miningEnabled: true,
    isPaused: false,
    emergencyPaused: false,
    lastUpdated: 0,
    contractVersion: '0',
    analytics: null
  });
  const [userStats, setUserStats] = useState({
    levels: [],
    canMine: false,
    vested: '0',
    totalClaimed: '0',
    claimable: '0',
    isSpecial: false,
    lastFetched: 0,
    contractVersion: '0'
  });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [txLoading, setTxLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const refreshIntervalRef = useRef(null);
  
  // Example of applying theme colors
  const themeColors = {
    primary: '#003049',
    secondary: '#000000',
    background: '#FFFFFF',
  };

  // Check if an address is a special address (frontend validation)
  const isSpecialAddress = useCallback(async (address) => {
    if (!dolphinContract || !address) return false;
    try {
      const specialAddresses = await dolphinContract.getSpecialAddresses();
      const isSpecial = specialAddresses.some(
        (addr) => addr.toLowerCase() === address.toLowerCase()
      );
      console.log(`[isSpecialAddress] Address ${address} is special:`, isSpecial);
      return isSpecial;
    } catch (err) {
      console.error('[isSpecialAddress] Error:', err);
      return false;
    }
  }, [dolphinContract]);
  
  // Helper: check if current user is special
  const isCurrentUserSpecial = useCallback(async () => {
    if (!account) return false;
    return await isSpecialAddress(account);
  }, [account, isSpecialAddress]);

  // ============ HELPER FUNCTIONS ============
  const formatUSDT = useCallback((value) => {
    let formatted = '0';
    try {
      if (value === null || value === undefined || isNaN(value) || value === '' || value === false) return '0';
      formatted = ethers.utils.formatUnits(value, usdtDecimals);
      if (isNaN(formatted) || !isFinite(formatted)) return '0';
      return formatted;
    } catch {
      return '0';
    }
  }, [usdtDecimals]);

  const formatTokens = useCallback((value) => {
    let formatted = '0';
    try {
      if (value === null || value === undefined || isNaN(value) || value === '' || value === false) return '0';
      formatted = ethers.utils.formatEther(value);
      if (isNaN(formatted) || !isFinite(formatted)) return '0';
      return formatted;
    } catch {
      return '0';
    }
  }, []);

  const parseUSDT = useCallback((value) => {
    return ethers.utils.parseUnits(value.toString(), usdtDecimals);
  }, [usdtDecimals]);

  const safeViewCall = useCallback(async (methodName, ...args) => {
    // Use dolphinReferralContract for referral-only methods
    const referralMethods = [
      'getDownlinesByRank',
      'userReferralEarnings',
      'addressToInviteCode',
      'referrerOf',
      'isUser'
    ];
    let contract = dolphinContract;
    if (referralMethods.includes(methodName) && dolphinReferralContract) {
      contract = dolphinReferralContract;
    }
    if (!contract) return null;
    try {
      return await contract.callStatic[methodName](...args);
    } catch (err) {
      console.log(`[safeViewCall] ${methodName} failed:`, err.message);
      return null;
    }
  }, [dolphinContract, dolphinReferralContract]);

  const shortenAddress = useCallback((address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  }, []);

  const formatInviteCode = useCallback((code) => {
    if (!code || code === ethers.constants.HashZero) return '';
    const hex = ethers.utils.hexlify(code);
    return `${hex.substring(0, 8)}...${hex.substring(hex.length - 6)}`;
  }, []);

  const getMyReferralLink = useCallback(() => {
    if (!inviteCode) return '';
    const baseUrl = window.location.origin;
    const codeHex = ethers.utils.hexlify(inviteCode);
    return `${baseUrl}?ref=${codeHex}`;
  }, [inviteCode]);

  // ============ INITIALIZE CONTRACTS ============
  useEffect(() => {
    const initContracts = async () => {
      if (!provider || !CONTRACTS.DOLPHIN || !CONTRACTS.USDT || !CONTRACTS.DOLPHIN_REFERRAL || !CONTRACTS.DOLPHIN_PAYMENTS || !CONTRACTS.DOLPHIN_VESTING) return;
      try {
        const dolphin = new ethers.Contract(CONTRACTS.DOLPHIN, DOLPHIN_ABI, provider);
        const usdt = new ethers.Contract(CONTRACTS.USDT, USDT_ABI, provider);
        const referral = new ethers.Contract(CONTRACTS.DOLPHIN_REFERRAL, DOLPHIN_REFERRAL_ABI, provider);
        const payments = new ethers.Contract(CONTRACTS.DOLPHIN_PAYMENTS, DOLPHIN_PAYMENTS_ABI, provider);
        const vesting = new ethers.Contract(CONTRACTS.DOLPHIN_VESTING, DOLPHIN_VESTING_ABI, provider);
        // DolphinExtended contract uses same address as CONTRACTS.DOLPHIN (see config.js comment)
        const dolphinExtended = new ethers.Contract(CONTRACTS.DOLPHIN, DOLPHIN_EXTENDED_ABI, provider);
        const decimals = await usdt.decimals();
        setUsdtDecimals(decimals);
        setDolphinContract(dolphin);
        setUsdtContract(usdt);
        setDolphinReferralContract(referral);
        setDolphinPaymentsContract(payments);
        setDolphinVestingContract(vesting);
        setDolphinExtendedContract(dolphinExtended);
        // setShowDecimals(true); // removed, variable no longer exists
        console.log('[useDolphin] Contracts initialized, USDT decimals:', decimals);
      } catch (err) {
        console.error('[useDolphin] Contract init error:', err);
      }
    };
    initContracts();
  }, [provider]);

  // ============ CHECK REFERRAL ON PAGE LOAD ============
  useEffect(() => {
    const checkUrlForReferral = async () => {
      if (!dolphinContract || !account) return;
      
      // Check localStorage first
      const savedReferral = localStorage.getItem('pendingReferral');
      const urlParams = new URLSearchParams(window.location.search);
      const refParam = urlParams.get('ref') || savedReferral;
      
      if (refParam) {
        try {
          // Convert hex string to bytes32
          let inviteCode;
          try {
            inviteCode = ethers.utils.hexZeroPad(refParam, 32);
          } catch {
            // If it's already bytes32, use as is
            inviteCode = refParam;
          }
          
          // Check if code is valid
          const referrerAddress = await dolphinContract.inviteCodeToAddress(inviteCode);
          
          if (referrerAddress !== ethers.constants.AddressZero) {
            console.log('[Referral] Valid referral code found:', referrerAddress);
            
            // Check if user already has referrer
            const userReferrer = await dolphinContract.referrerOf(account);
            
            if (userReferrer === ethers.constants.AddressZero) {
              // User doesn't have referrer yet - store pending referral
              setPendingReferral({
                code: inviteCode,
                referrer: referrerAddress,
                codeString: refParam.length > 12 ? refParam.substring(0, 12) + '...' : refParam
              });
              
              // Store in localStorage for later
              localStorage.setItem('pendingReferral', refParam);
            }
          } else {
            console.log('[Referral] Invalid referral code');
            localStorage.removeItem('pendingReferral');
          }
        } catch (error) {
          console.log('[Referral] Error checking referral code:', error);
          localStorage.removeItem('pendingReferral');
        }
      }
    };
    
    checkUrlForReferral();
  }, [dolphinContract, account]);

  // ============ FETCH GLOBAL STATS ============
  const fetchStats = useCallback(async () => {
    if (!dolphinContract) return;

    setLoading(true);
    try {
      
      const [
        minedTokens,
        currentPriceUSDT,
        miningEnded,
        miningEnabled,
        isPaused,
        emergencyPaused,
        contractVersion,
        analytics,
        startPriceResult
      ] = await Promise.all([
        safeViewCall('minedTokens'),
        safeViewCall('getCurrentPriceUSDT'),
        safeViewCall('miningEnded'),
        safeViewCall('miningEnabled'),
        safeViewCall('isPaused'),
        safeViewCall('emergencyPaused'),
        safeViewCall('contractVersion'),
        safeViewCall('analytics'),
        safeViewCall('getStartPrice')
      ]);
      console.log('[fetchStats] Raw startPriceResult:', startPriceResult);
      setStats(prev => ({
        ...prev,
        minedTokens: formatTokens(minedTokens || 0),
        currentPrice: formatUSDT(currentPriceUSDT || 0),
        miningEnded: !!miningEnded,
        miningEnabled: !!miningEnabled,
        isPaused: !!isPaused,
        emergencyPaused: !!emergencyPaused,
        contractVersion: contractVersion?.toString() || '0',
        analytics: analytics || null,
        startPrice: formatUSDT(startPriceResult || 0),
        lastUpdated: Date.now(),
      }));
      console.log('[fetchStats] Analytics:', analytics);
    } catch (err) {
      console.error('[fetchStats] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [dolphinContract, formatTokens, formatUSDT, safeViewCall]);

  // ============ FETCH USER STATS ============
  const fetchUserStats = useCallback(async () => {
    if (!dolphinContract || !account) {
      setUserStats(prev => ({ ...prev, levels: [] }));
      return;
    }

    try {
      console.log('[fetchUserStats] Fetching user data...');

      // Use DolphinExtended contract for isSpecialAddress
      const [
        canMineStatus,
        vestingInfo,
        userInfo,
        claimableDolp
      ] = await Promise.all([
        safeViewCall('canMine', account),
        safeViewCall('vestingInfo', account),
        safeViewCall('getUserInfo', account),
        dolphinVestingContract ? dolphinVestingContract.getClaimableDolp(account) : Promise.resolve('0')
      ]);

      let isSpecial = false;
      if (dolphinExtendedContract) {
        try {
          isSpecial = await dolphinExtendedContract.isSpecialAddress(account);
        } catch (err) {
          console.error('[fetchUserStats] Error calling isSpecialAddress on DolphinExtended:', err);
        }
      }
      // Log special status
      console.log('[fetchUserStats] isSpecial (DolphinExtended):', isSpecial);

      // Get levels from user info if available
      let userLevels = [];
      if (isSpecial) {
        userLevels = [1,2,3,4,5,6,7,8,9,10,11,12];
        console.log('[fetchUserStats] User is special, all levels unlocked:', userLevels);
      } else if (userInfo && userInfo[4]) {
        userLevels = userInfo[4].map(level => level.toNumber());
      } else {
        // Fallback: Check all 12 levels
        for (let i = 1; i <= 12; i++) {
          try {
            const hasLevel = await safeViewCall('hasLevel', account, i);
            if (hasLevel) userLevels.push(i);
          } catch (err) {
            console.log(`[fetchUserStats] Error checking level ${i}:`, err.message);
          }
        }
      }

      // Calculate DOLP stats
      const totalVested = vestingInfo?.totalVested || 0;
      const totalClaimed = vestingInfo?.totalClaimed || 0;
      const claimable = claimableDolp || 0;
      const pending = totalVested - totalClaimed - claimable;

      setUserStats({
        levels: userLevels,
        canMine: canMineStatus || false,
        vested: formatTokens(totalVested),
        totalClaimed: formatTokens(totalClaimed),
        claimable: formatTokens(claimable),
        pending: formatTokens(pending > 0 ? pending : 0),
        isSpecial: isSpecial || false,
        lastFetched: Date.now(),
      });
      console.log('[fetchUserStats] User data updated. Levels:', userLevels);
    } catch (err) {
      console.error('[fetchUserStats] Error:', err);
    }
  }, [dolphinContract, dolphinVestingContract, account, formatTokens, safeViewCall, dolphinExtendedContract ]);

  // ============ FETCH REFERRAL STATS ============
  const fetchReferralStats = useCallback(async () => {
    if (!dolphinReferralContract || !account) return;
    console.log('[fetchReferralStats] Using DolphinReferral contract for account:', account);
    try {
      const [
        referralEarnings,
        referrer,
        userInviteCode,
        downlinesByRank
      ] = await Promise.all([
        dolphinReferralContract.userReferralEarnings(account),
        dolphinReferralContract.referrerOf(account),
        dolphinReferralContract.addressToInviteCode(account),
        dolphinReferralContract.getDownlinesByRank(account, 5)
      ]);

      // Defensive: fallback check for Level 1 (optional, can use DolphinExtended if needed)
      let hasLevel1 = true;

      // Downlines by rank (array of arrays)
      let totalDownlines = 0;
      let detailedDownlines = [];
      if (Array.isArray(downlinesByRank)) {
        downlinesByRank.forEach((rankArr, idx) => {
          totalDownlines += Array.isArray(rankArr) ? rankArr.length : 0;
          if (Array.isArray(rankArr)) {
            detailedDownlines = detailedDownlines.concat(rankArr.map(addr => ({
              address: addr,
              rank: idx + 1
            })));
          }
        });
      }

      // Set user's invite code
      if (userInviteCode && userInviteCode !== ethers.constants.HashZero) {
        setInviteCode(userInviteCode);
      }

      setReferralStats({
        totalEarned: formatUSDT(referralEarnings),
        downlines: detailedDownlines,
        downlineCount: totalDownlines,
        directDownlineCount:
          Array.isArray(downlinesByRank) && Array.isArray(downlinesByRank[0])
            ? downlinesByRank[0].length
            : 0,
        canEarn: hasLevel1,
        hasReferrer: referrer !== ethers.constants.AddressZero,
        referrer: referrer !== ethers.constants.AddressZero ? referrer : null,
        inviteCode: userInviteCode && userInviteCode !== ethers.constants.HashZero ? userInviteCode : null,
        downlinesByRank: Array.isArray(downlinesByRank) ? downlinesByRank : [[], [], [], [], []],
      });
      console.log('[fetchReferralStats] Referral stats set (DolphinReferral):', {
        totalEarned: formatUSDT(referralEarnings),
        downlines: detailedDownlines,
        downlineCount: totalDownlines,
        directDownlineCount:
          Array.isArray(downlinesByRank) && Array.isArray(downlinesByRank[0])
            ? downlinesByRank[0].length
            : 0,
        canEarn: hasLevel1,
        hasReferrer: referrer !== ethers.constants.AddressZero,
        referrer: referrer !== ethers.constants.AddressZero ? referrer : null,
        inviteCode: userInviteCode && userInviteCode !== ethers.constants.HashZero ? userInviteCode : null,
        downlinesByRank: Array.isArray(downlinesByRank) ? downlinesByRank : [[], [], [], [], []],
      });
    } catch (error) {
      console.error('[fetchReferralStats] Error (DolphinReferral):', error);
    }
  }, [dolphinReferralContract, account, formatUSDT]);

  // ============ FETCH FULL DOWNLINE TREE ============
  /**
   * Fetches the full downline tree for a given root address (default: current user)
   * Uses getDownlinesByRank to get all downlines, then builds the tree using referrerOf
   * @param {number} maxDepth
   * @param {number} maxBreadth
   * @param {string} [rootAddress] - Optional root address to build tree from
   */
  const fetchFullDownlineTree = useCallback(async (maxDepth = 3, maxBreadth = 50, rootAddress) => {
    if (!dolphinContract || !(account || rootAddress)) {
      return null;
    }

    setDownlineTree(prev => ({ ...prev, loading: true, error: null }));
    try {
      const startAddress = rootAddress || account;
      console.log('[fetchFullDownlineTree] Building downline tree using getDownlinesByRank/referrerOf for', startAddress);
      // Removed unused cache variable

      // Get all downlines up to maxDepth
      let allDownlines = [];
      try {
        const downlinesByRank = await safeViewCall('getDownlinesByRank', startAddress, maxDepth);
        if (Array.isArray(downlinesByRank)) {
          for (let arr of downlinesByRank) {
            if (Array.isArray(arr)) {
              allDownlines.push(...arr);
            }
          }
        }
      } catch (error) {
        console.log(`[fetchFullDownlineTree] Error fetching getDownlinesByRank for ${startAddress}:`, error.message);
      }

      // Remove duplicates
      allDownlines = [...new Set(allDownlines.map(a => a.toLowerCase()))];

      // Map: address -> referrer
      const referrerMap = {};
      for (const addr of allDownlines) {
        try {
          const ref = await safeViewCall('referrerOf', addr);
          referrerMap[addr] = ref ? ref.toLowerCase() : null;
        } catch (e) {
          referrerMap[addr] = null;
        }
      }

      // Build tree recursively
      const buildTree = (userAddress, currentDepth = 0, path = []) => {
        if (currentDepth >= maxDepth || path.includes(userAddress.toLowerCase())) {
          return {
            address: userAddress,
            level: '0',
            hasChildren: false,
            children: [],
            depth: currentDepth
          };
        }
        // Find direct children (downlines whose referrer is this user)
        const directDownlines = allDownlines.filter(addr => referrerMap[addr] === userAddress.toLowerCase()).slice(0, maxBreadth);
        const children = directDownlines.map(childAddr => buildTree(childAddr, currentDepth + 1, [...path, userAddress.toLowerCase()]));
        return {
          address: userAddress,
          level: '0',
          hasChildren: children.length > 0,
          children,
          directCount: directDownlines.length,
          totalCount: children.reduce((sum, child) => sum + (child.totalCount || 0) + 1, 0),
          depth: currentDepth,
          isCurrentUser: userAddress.toLowerCase() === account.toLowerCase(),
          path: [...path]
        };
      };

      const tree = buildTree(startAddress);
      setDownlineTree({
        loading: false,
        error: null,
        data: tree,
        lastUpdated: Date.now()
      });
      console.log('[fetchFullDownlineTree] Tree built successfully');
      return tree;
    } catch (error) {
      console.error('[fetchFullDownlineTree] Error:', error);
      setDownlineTree(prev => ({ 
        ...prev, 
        loading: false, 
        error: 'Failed to fetch downline tree' 
      }));
      return null;
    }
  }, [dolphinContract, account, safeViewCall]);

  // ============ GET DOWNLINE DETAILS ============
  const getDownlineDetails = useCallback(async (downlineAddress) => {
    if (!dolphinContract || !downlineAddress) return null;
    
    try {
      const [
        userInfo,
        referralEarnings,
        downlinesArray
      ] = await Promise.all([
        safeViewCall('getUserInfo', downlineAddress),
        safeViewCall('userReferralEarnings', downlineAddress),
        safeViewCall('downlines', downlineAddress)
      ]);
      
      return {
        address: downlineAddress,
        levels: userInfo?.[4] || [],
        referralEarnings: formatUSDT(referralEarnings || 0),
        directDownlines: downlinesArray?.length || 0,
        canMine: userInfo?.[5] || false,
        hasReferrer: userInfo?.[1] || false,
        referrer: userInfo?.[2] || null,
        isActive: userInfo?.[0] || false
      };
    } catch (error) {
      console.log('[getDownlineDetails] Error:', error);
      return null;
    }
  }, [dolphinContract, formatUSDT, safeViewCall]);

  // ============ FETCH TRANSACTION HISTORY ============
  const fetchTransactionHistory = useCallback(async (limit = 50) => {
    if (!dolphinContract || !account || !provider) return [];
    setHistoryLoading(true);

    // Move processHistoryEvent inside useCallback to fix ESLint warnings
    const processHistoryEvent = async (event) => {
      if (!provider) return null;
      try {
        const block = await provider.getBlock(event.blockNumber);
        const timestamp = block.timestamp * 1000;
        switch (event.event) {
          case 'LevelPurchased':
            return {
              type: HISTORY_TYPES.LEVEL_PURCHASE,
              title: `Level ${event.args.level.toString()} Purchased`,
              description: `Purchased Level ${event.args.level.toString()}`,
              amount: formatUSDT(event.args.amountUSDT),
              amountRaw: event.args.amountUSDT,
              timestamp,
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash,
              status: 'completed',
              icon: '🎯',
              color: '#10b981',
              level: event.args.level.toString()
            };
          case 'TokensMined':
            return {
              type: HISTORY_TYPES.MINING,
              title: 'Tokens Mined',
              description: `Mined ${formatTokens(event.args.tokenAmount)} DOLP`,
              amount: formatUSDT(event.args.usdtAmount),
              amountRaw: event.args.usdtAmount,
              timestamp,
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash,
              status: 'completed',
              icon: '⛏️',
              color: '#f59e0b',
              tokenAmount: formatTokens(event.args.tokenAmount)
            };
          case 'MLMPayout':
            return {
              type: HISTORY_TYPES.REFERRAL_EARNINGS,
              title: 'Referral Earnings',
              description: `Earned from ${shortenAddress(event.args.from)} (Level ${event.args.level})`,
              amount: formatUSDT(event.args.amountUSDT),
              amountRaw: event.args.amountUSDT,
              timestamp,
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash,
              status: 'pending',
              icon: '💰',
              color: '#8b5cf6',
              from: event.args.from,
              level: event.args.level.toString()
            };
          case 'FixedPayout':
          case 'MiningPayout':
            return {
              type: HISTORY_TYPES.PULL_PAYMENT,
              title: event.event === 'FixedPayout' ? 'Fixed Distribution' : 'Mining Distribution',
              description: `From ${shortenAddress(event.args.from)}`,
              amount: formatUSDT(event.args.amountUSDT),
              amountRaw: event.args.amountUSDT,
              timestamp,
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash,
              status: 'pending',
              icon: '🏦',
              color: '#3b82f6',
              from: event.args.from
            };
          case 'ReferrerSet':
            return {
              type: HISTORY_TYPES.REFERRAL_SET,
              title: 'Referrer Set',
              description: `Set ${shortenAddress(event.args.referrer)} as referrer`,
              amount: null,
              timestamp,
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash,
              status: 'completed',
              icon: '🤝',
              color: '#6366f1',
              referrer: event.args.referrer
            };
          case 'InviteCodeGenerated':
            return {
              type: HISTORY_TYPES.INVITE_GENERATED,
              title: 'Invite Code Generated',
              description: 'Generated your referral invite code',
              amount: null,
              timestamp,
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash,
              status: 'completed',
              icon: '🔑',
              color: '#ec4899'
            };
          case 'NewDownline':
            return {
              type: HISTORY_TYPES.NEW_DOWNLINE,
              title: 'New Downline Added',
              description: `${shortenAddress(event.args.downline)} joined your team (Level ${event.args.level})`,
              amount: null,
              timestamp,
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash,
              status: 'completed',
              icon: '👥',
              color: '#3b82f6',
              downlineAddress: event.args.downline,
              level: event.args.level.toString()
            };
          case 'TokensClaimed':
            return {
              type: HISTORY_TYPES.TOKEN_CLAIM,
              title: 'Tokens Claimed',
              description: `Claimed ${formatTokens(event.args.amount)} DOLP`,
              amount: formatTokens(event.args.amount),
              amountRaw: event.args.amount,
              timestamp,
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash,
              status: 'completed',
              icon: '💎',
              color: '#10b981'
            };
          default:
            return null;
        }
      } catch (error) {
        console.log('[processHistoryEvent] Error:', error);
        return null;
      }
    };

    try {
      console.log('[History] Fetching transaction history...');
      const eventFilters = [
        dolphinContract.filters.LevelPurchased(account),
        dolphinContract.filters.TokensMined(account),
        dolphinContract.filters.MLMPayout(null, account),
        dolphinContract.filters.FixedPayout(null, account),
        dolphinContract.filters.MiningPayout(null, account),
        dolphinContract.filters.ReferrerSet(account),
        dolphinContract.filters.InviteCodeGenerated(account),
        dolphinContract.filters.NewDownline(account),
        dolphinContract.filters.TokensClaimed(account)
      ];

      const allEvents = [];
      for (const filter of eventFilters) {
        try {
          const events = await dolphinContract.queryFilter(filter, 0, 'latest');
          allEvents.push(...events);
        } catch (error) {
          console.log('[History] Error fetching events:', error.message);
        }
      }

      allEvents.sort((a, b) => b.blockNumber - a.blockNumber);
      const historyItems = [];

      for (const event of allEvents.slice(0, limit)) {
        try {
          const historyItem = await processHistoryEvent(event);
          if (historyItem) historyItems.push(historyItem);
        } catch (error) {
          console.log('[History] Error processing event:', error);
        }
      }

      setTransactionHistory(historyItems);
      console.log('[History] Found', historyItems.length, 'history items');
      // Log each history item in detail
      historyItems.forEach((item, idx) => {
        console.log(`[History Item ${idx + 1}]`, item);
      });
      return historyItems;
    } catch (error) {
      console.error('[fetchTransactionHistory] Error:', error);
      return [];
    } finally {
      setHistoryLoading(false);
    }
  }, [dolphinContract, account, provider, formatUSDT, formatTokens, shortenAddress, setTransactionHistory, setHistoryLoading]);
  // ============ PROCESS HISTORY EVENT ============
  // (removed duplicate processHistoryEvent declaration)

  // (removed duplicate processHistoryEvent declaration)

  // ============ GENERATE INVITE CODE ============

  const generateInviteCode = async () => {
    if (!account || !signer) {
      throw new Error('Please connect your wallet');
    }

    if (!CONTRACTS.DOLPHIN_REFERRAL) {
      throw new Error('DolphinReferral contract address not set');
    }

    const referralWithSigner = new ethers.Contract(
      CONTRACTS.DOLPHIN_REFERRAL,
      [
        'function generateInviteCode() returns (bytes32)',
        'function getMyInviteCode(address) view returns (bytes32)'
      ],
      signer
    );

    setTxLoading(true);
    setActionError(null);

    try {
      // Check if user already has an invite code
      const existingCode = await referralWithSigner.getMyInviteCode(account);
      if (existingCode && existingCode !== ethers.constants.HashZero) {
        setInviteCode(existingCode);
        setSuccessMessage('You already have an invite code!');
        return existingCode;
      }

      // Always attempt to generate invite code, regardless of level
      console.log('[Referral] Generating invite code (DolphinReferral)...');

      // Estimate gas
      let gasLimit;
      try {
        const estimatedGas = await referralWithSigner.estimateGas.generateInviteCode();
        gasLimit = estimatedGas.mul(120).div(100);
        console.log('[Referral] Estimated gas:', gasLimit.toString());
      } catch {
        gasLimit = 200000;
      }

      let generatedCode;
      let tx, receipt;
      try {
        tx = await referralWithSigner.generateInviteCode({ gasLimit });
        console.log('[Referral] TX sent:', tx.hash);
        setSuccessMessage('Generating invite code...');
        receipt = await tx.wait();
        if (receipt.status !== 1) throw new Error('Transaction failed');
      } catch (txError) {
        // If contract reverts with 'AICD' (already has invite code), continue to fetch code
        if (txError?.errorName === 'Error' || txError?.message?.includes('AICD') || txError?.message?.includes('already has invite code')) {
          console.warn('[Referral] Already has invite code, fetching existing code...');
        } else if (txError?.message?.includes('Invite code already exists')) {
          setActionError('You already have an invite code.');
          return;
        } else if (txError?.message?.includes('revert') && txError?.message?.includes('level')) {
          setActionError('You must buy Level 1 before you can generate an invite code.');
          return;
        } else {
          setActionError('Failed to generate invite code. Please check your eligibility and try again.');
          return;
        }
      }

      // Always fetch the latest code from the contract
      generatedCode = await referralWithSigner.getMyInviteCode(account);
      setInviteCode(generatedCode);
      console.log('[Referral] Invite code:', ethers.utils.hexlify(generatedCode));
      setSuccessMessage('✅ Invite code generated successfully!');
      await fetchReferralStats();
      return generatedCode;
    } catch (error) {
      console.error('[generateInviteCode] Error:', error);
      let userMessage = 'Failed to generate invite code.';
      if (error.code === 4001) {
        userMessage = 'Transaction rejected by user.';
      } else if (error.message?.includes('AICD') || error.message?.includes('already has invite code')) {
        userMessage = 'You already have an invite code.';
      } else if (error.message?.toLowerCase().includes('level') || error.message?.toLowerCase().includes('not eligible')) {
        userMessage = 'You must buy Level 1 before you can generate an invite code.';
      } else if (error.message?.toLowerCase().includes('paused')) {
        userMessage = 'The contract is currently paused. Please try again later.';
      } else if (error.message?.toLowerCase().includes('user denied transaction')) {
        userMessage = 'You denied the transaction.';
      } else if (error.message?.toLowerCase().includes('insufficient funds')) {
        userMessage = 'Insufficient funds to pay for gas.';
      } else if (error.message?.toLowerCase().includes('network')) {
        userMessage = 'Network error. Please check your connection and try again.';
      } else if (error.data?.message) {
        userMessage = error.data.message;
      }
      setActionError(userMessage);
      throw new Error(userMessage);
    } finally {
      setTxLoading(false);
    }
  };

  // ============ GET TOTAL SPENT USDT ============
  /**
   * Fetches the total USDT spent by a user from the contract
   * @param {string} userAddress
   * @returns {Promise<string>} Total spent in USDT (as string, 6 decimals)
   */
  const getTotalSpentUSDT = useCallback(async (userAddress) => {
    if (!dolphinContract || !userAddress) return '0';
    try {
      const spent = await dolphinContract.getTotalSpentUSDT(userAddress);
      return formatUSDT(spent);
    } catch (err) {
      console.error('[getTotalSpentUSDT] Error:', err);
      return '0';
    }
  }, [dolphinContract, formatUSDT]);

  // ============ SET REFERRER ============
  const setUserReferrer = async (inviteCode) => {
    // Sanitize inviteCode to ensure it's a valid bytes32
    let code = inviteCode;
    try {
      if (typeof code === 'string') {
        if (!code.startsWith('0x')) {
          // If it's a plain string, format to bytes32
          code = ethers.utils.formatBytes32String(code);
        } else {
          // If it's a hex string, pad to 32 bytes
          code = ethers.utils.hexZeroPad(code, 32);
        }
      }
    } catch (e) {
      throw new Error('Invalid invite code format');
    }
    if (!account || !signer) {
      throw new Error('Please connect your wallet');
    }
    if (!CONTRACTS.DOLPHIN_REFERRAL) {
      throw new Error('DolphinReferral contract address not set');
    }

    // Use DolphinReferral contract for all referral actions
    const referralWithSigner = new ethers.Contract(
      CONTRACTS.DOLPHIN_REFERRAL,
      [
        'function setReferrer(bytes32 inviteCode)',
        'function referrerOf(address) view returns (address)',
        'function inviteCodeToAddress(bytes32) view returns (address)'
      ],
      signer
    );

    setTxLoading(true);
    setActionError(null);

    try {
      console.log('[Referral] Setting referrer (DolphinReferral)...');

      // Check if user already has referrer
      const currentReferrer = await referralWithSigner.referrerOf(account);
      if (currentReferrer !== ethers.constants.AddressZero) {
        throw new Error('You already have a referrer');
      }

      // Validate invite code

      const referrerAddress = await referralWithSigner.inviteCodeToAddress(code);
      if (referrerAddress === ethers.constants.AddressZero) {
        throw new Error('Invalid invite code');
      }

      if (referrerAddress.toLowerCase() === account.toLowerCase()) {
        throw new Error('Cannot refer yourself');
      }

      // Estimate gas with robust fallback
      let gasLimit;
      let tx, receipt;
      try {
        const estimatedGas = await referralWithSigner.estimateGas.setReferrer(code);
        gasLimit = estimatedGas.mul(120).div(100);
        tx = await referralWithSigner.setReferrer(code, { gasLimit });
      } catch (gasErr) {
        console.warn('[Referral] Gas estimation failed:', gasErr);
        try {
          // Try higher fallback
          gasLimit = 300000;
          tx = await referralWithSigner.setReferrer(code, { gasLimit });
        } catch (fallbackErr) {
          console.warn('[Referral] Fallback gas limit failed:', fallbackErr);
          try {
            // Try with no gas limit as last resort
            tx = await referralWithSigner.setReferrer(code);
          } catch (finalErr) {
            console.error('[Referral] setReferrer failed completely:', finalErr);
            throw new Error('Failed to set referrer: ' + (finalErr?.reason || finalErr?.message || 'Unknown error'));
          }
        }
      }
      console.log('[Referral] TX sent:', tx.hash);
      setSuccessMessage('Setting referrer...');
      receipt = await tx.wait();
      if (receipt.status === 1) {
        console.log('[Referral] Referrer set successfully');
        setPendingReferral(null);
        localStorage.removeItem('pendingReferral');
        await Promise.all([fetchReferralStats(), fetchUserStats()]);
        setSuccessMessage('✅ Referrer set successfully!');
        return true;
      } else {
        throw new Error('Transaction failed');
      }
    } catch (error) {
      console.error('[setUserReferrer] Error:', error);

      let userMessage = 'Failed to set referrer';
      if (error.code === 4001) userMessage = 'Transaction rejected';
      if (error.message?.includes('Referrer already set')) userMessage = 'You already have a referrer';
      if (error.message?.includes('Invalid invite code')) userMessage = 'Invalid invite code';
      if (error.message?.includes('Cannot refer yourself')) userMessage = 'Cannot refer yourself';

      setActionError(userMessage);
      throw new Error(userMessage);
    } finally {
      setTxLoading(false);
    }
  };

  // ============ GAS ESTIMATION ============
  const estimateGasWithFallback = async (contract, methodName, args = [], fallbackGasByLevel = {}) => {
    try {
      console.log(`[Gas Estimation] Estimating gas for ${methodName}...`);
      
      const estimatedGas = await contract.estimateGas[methodName](...args);
      console.log(`[Gas Estimation] Estimated: ${estimatedGas.toString()} gas`);
      
      const gasWithBuffer = estimatedGas.mul(125).div(100);
      console.log(`[Gas Estimation] With 25% buffer: ${gasWithBuffer.toString()} gas`);
      
      return gasWithBuffer;
      
    } catch (gasError) {
      console.log(`[Gas Estimation] Failed: ${gasError.message}`);
      
      if (methodName === 'buyLevel' && args[0]) {
        const level = args[0];
        const fallbackGas = fallbackGasByLevel[level] || 2500000;
        console.log(`[Gas Estimation] Using fallback: ${fallbackGas} gas for Level ${level}`);
        return ethers.BigNumber.from(fallbackGas);
      }
      
      console.log(`[Gas Estimation] Using default fallback: 2,000,000 gas`);
      return ethers.BigNumber.from(2000000);
    }
  };

  // ============ PURCHASE LEVEL WITH REFERRAL CHECK ============
  const purchaseLevel = async (levelId) => {
    if (!account || !signer) throw new Error('Please connect your wallet');
    if (levelId < 1 || levelId > 12) throw new Error('Invalid level');
        // --- BEGIN: USDT Balance, Allowance, and Approval Logic ---
        if (!usdtContract) throw new Error('USDT contract not initialized');
        // Get required amount for the level
        let requiredAmount = ethers.BigNumber.from('0');
        if (dolphinContract) {
          try {
            const rawPrice = await dolphinContract.getLevelPriceUSDT(levelId);
            requiredAmount = rawPrice;
          } catch (e) {
            console.error('[Purchase] Failed to fetch level price:', e);
          }
        }
        // Get user USDT balance and allowance
        let usdtBalance = ethers.BigNumber.from('0');
        let usdtAllowance = ethers.BigNumber.from('0');
        try {
          usdtBalance = await usdtContract.balanceOf(account);
          usdtAllowance = await usdtContract.allowance(account, dolphinContract.address);
        } catch (e) {
          console.error('[Purchase] Failed to fetch USDT balance/allowance:', e);
        }
        console.log('[Purchase] USDT balance:', usdtBalance.toString());
        console.log('[Purchase] USDT allowance:', usdtAllowance.toString());
        console.log('[Purchase] Required amount for level', levelId, ':', requiredAmount.toString());
        // If allowance is insufficient, prompt approval
        if (usdtAllowance.lt(requiredAmount)) {
          try {
            const usdtWithSigner = new ethers.Contract(CONTRACTS.USDT, USDT_ABI, signer);
            const approveTx = await usdtWithSigner.approve(dolphinContract.address, requiredAmount);
            console.log('[Purchase] Approving USDT... TX:', approveTx.hash);
            await approveTx.wait();
            console.log('[Purchase] USDT approved.');
          } catch (e) {
            console.error('[Purchase] USDT approval failed:', e);
            throw new Error('USDT approval failed');
          }
        }
        // --- END: USDT Balance, Allowance, and Approval Logic ---
    
    setTxLoading(true);
    setActionError(null);
    setSuccessMessage(null);
    
    try {
      console.log('=== STARTING LEVEL PURCHASE ===');
      console.log(`[Purchase] Buying Level ${levelId}`);

      if (!account || !signer) {
        throw new Error('Please connect your wallet');
      }

      // Get isPaused from stats state
      if (stats && stats.isPaused) throw new Error('Contract is paused');

      const dolphinWithSigner = new ethers.Contract(
        CONTRACTS.DOLPHIN,
        [
          'function buyLevel(uint256 level)',
          'function hasLevel(address,uint256) view returns (bool)'
        ],
        signer
      );

      setTxLoading(true);
      setActionError(null);

      const alreadyHasLevel = await dolphinWithSigner.hasLevel(account, levelId);
      if (alreadyHasLevel) throw new Error(`You already own Level ${levelId}`);

      if (levelId > 1) {
        for (let i = 1; i < levelId; i++) {
          const hasPrevLevel = await dolphinWithSigner.hasLevel(account, i);
          if (!hasPrevLevel) {
            throw new Error(`You must purchase Level ${i} before Level ${levelId}`);
          }
        }
      }

      console.log('[Purchase] All checks passed!');

      // 6. Estimate gas
      const fallbackGasByLevel = {
        1: 2200000, 2: 2300000, 3: 2400000, 4: 2500000,
        5: 2600000, 6: 2700000, 7: 2800000, 8: 2900000,
        9: 3000000, 10: 3100000, 11: 3200000, 12: 3300000,
      };

      const gasLimit = await estimateGasWithFallback(
        dolphinWithSigner,
        'buyLevel',
        [levelId],
        fallbackGasByLevel
      );

      console.log(`[Purchase] Final gas limit: ${gasLimit.toString()}`);

      // 7. Send transaction
      console.log(`[Purchase] Sending buyLevel transaction...`);
      setSuccessMessage('Sending transaction... Please confirm in wallet');

      // Always send value: 0 to avoid sending MATIC/ETH
      const tx = await dolphinWithSigner.buyLevel(levelId, { gasLimit, value: 0 });
      console.log(`[Purchase] Transaction sent:`, tx.hash);
      setSuccessMessage('Transaction sent! Waiting for confirmation...');

      const receipt = await tx.wait();
      console.log(`[Purchase] Transaction confirmed in block ${receipt.blockNumber}`);
      console.log(`[Purchase] Gas used: ${receipt.gasUsed.toString()}`);
      console.log(`[Purchase] Status:`, receipt.status === 1 ? '✅ SUCCESS' : '❌ FAILED');

      if (receipt.status === 1) {
        console.log(`[Purchase] Purchase successful!`);

        await Promise.all([
          fetchUserStats(),
          fetchStats(),
          fetchReferralStats(),
          fetchTransactionHistory(),
          fetchFullDownlineTree(2)
        ]);

        setSuccessMessage(`🎉 Successfully purchased Level ${levelId}!`);

        // Use getLevelPriceUSDT to get price, and usdtDecimals for formatting
        let price = '0';
        if (dolphinContract) {
          try {
            const rawPrice = await dolphinContract.getLevelPriceUSDT(levelId);
            price = ethers.utils.formatUnits(rawPrice, usdtDecimals);
          } catch {}
        }

        return {
          success: true,
          txHash: tx.hash,
          level: levelId,
          price,
          gasUsed: receipt.gasUsed.toString(),
        };
      } else {
        throw new Error('Transaction reverted');
      }

    } catch (err) {
      console.error(`[purchaseLevel] Error:`, err);
      let userMessage = 'Transaction failed';
      // Try to parse revert reason from error data
      if (err.code === 4001) userMessage = 'Transaction rejected by user';
      else if (err.message?.includes('insufficient funds')) userMessage = 'Insufficient MATIC for gas fees';
      else if (err.message?.includes('Insufficient USDT')) userMessage = err.message;
      else if (err.message?.includes('already own')) userMessage = 'You already own this level';
      else if (err.message?.includes('must purchase')) userMessage = err.message;
      else if (err.message?.includes('Contract is paused')) userMessage = err.message;
      else if (err.code === 'UNPREDICTABLE_GAS_LIMIT') userMessage = 'Gas estimation failed. Try approving more USDT first.';
      else if (err?.errorName === 'Error' && err?.data) {
        // ethers v5: error.data contains revert reason as hex
        try {
          const reason = ethers.utils.toUtf8String(err.data.slice(138));
          userMessage = `Contract reverted: ${reason}`;
        } catch {}
      } else if (err?.reason) {
        userMessage = `Contract reverted: ${err.reason}`;
      } else if (err?.data && typeof err.data === 'string') {
        // Try to decode revert reason from hex
        try {
          const reason = ethers.utils.toUtf8String(err.data.slice(138));
          userMessage = `Contract reverted: ${reason}`;
        } catch {}
      }
      setActionError(userMessage);
      throw new Error(userMessage);
    } finally {
      setTxLoading(false);
    }
  };

  

   // Multi-level purchase: sequentially purchase each level
  const purchaseLevels = async (levelIds, totalUsd) => {
    if (!Array.isArray(levelIds) || levelIds.length === 0) throw new Error('No levels selected');
    if (!usdtContract) throw new Error('USDT contract not initialized');
    if (!dolphinContract) throw new Error('Dolphin contract not initialized');
    if (!account || !signer) throw new Error('Please connect your wallet');
    // Defensive: remove duplicates and sort levels
    const uniqueSortedLevels = [...new Set(levelIds)].sort((a, b) => a - b);
    // Check contract pause
    if (typeof stats !== 'undefined' && stats && stats.isPaused) throw new Error('Contract is paused');
    let requiredAmount = ethers.BigNumber.from('0');
    try {
      for (const levelId of uniqueSortedLevels) {
        const rawPrice = await dolphinContract.getLevelPriceUSDT(levelId);
        requiredAmount = requiredAmount.add(rawPrice);
      }
    } catch (e) {
      console.error('[PurchaseLevels] Failed to fetch level prices:', e);
      throw new Error('Failed to fetch level prices');
    }
    // Detailed logging for batch purchase
    console.log('[PurchaseLevels] USDT balance:', (await usdtContract.balanceOf(account)).toString());
    console.log('[PurchaseLevels] USDT allowance:', (await usdtContract.allowance(account, dolphinContract.address)).toString());
    console.log('[PurchaseLevels] Required amount for levels', uniqueSortedLevels, ':', requiredAmount.toString());
    // Removed frontend USDT balance check; wallet will handle insufficient balance
    let usdtAllowance = await usdtContract.allowance(account, dolphinContract.address);
    if (usdtAllowance.lt(requiredAmount)) {
      try {
        const usdtWithSigner = new ethers.Contract(CONTRACTS.USDT, USDT_ABI, signer);
        const approveTx = await usdtWithSigner.approve(dolphinContract.address, requiredAmount);
        console.log('[PurchaseLevels] Approving USDT... TX:', approveTx.hash);
        await approveTx.wait();
        console.log('[PurchaseLevels] USDT approved.');
      } catch (e) {
        console.error('[PurchaseLevels] USDT approval failed:', e);
        throw new Error('USDT approval failed');
      }
    }
    setTxLoading(true);
    setActionError(null);
    setSuccessMessage(null);
    try {
      const dolphinWithSigner = dolphinContract.connect(signer);
      console.log('=== STARTING MULTI-LEVEL PURCHASE ===');
      console.log(`[PurchaseLevels] Buying Levels:`, uniqueSortedLevels);
      // Estimate gas for batch
      let gasLimit;
      try {
        gasLimit = await dolphinWithSigner.estimateGas.buyLevels(uniqueSortedLevels);
        gasLimit = gasLimit.mul(125).div(100);
        console.log(`[PurchaseLevels] Estimated gas:`, gasLimit.toString());
      } catch {
        gasLimit = 2500000;
        console.log('[PurchaseLevels] Using fallback gas limit:', gasLimit);
      }
      const tx = await dolphinWithSigner.buyLevels(uniqueSortedLevels, { gasLimit, value: 0 });
      console.log(`[PurchaseLevels] Transaction sent:`, tx.hash);
      setSuccessMessage(`Successfully purchased ${uniqueSortedLevels.length} level(s)!`);
      const receipt = await tx.wait();
      console.log(`[PurchaseLevels] Transaction confirmed in block ${receipt.blockNumber}`);
      console.log(`[PurchaseLevels] Gas used: ${receipt.gasUsed ? receipt.gasUsed.toString() : 'N/A'}`);
      console.log(`[PurchaseLevels] Status:`, receipt.status === 1 ? '✅ SUCCESS' : '❌ FAILED');
      await refreshAllData();
      return {
        success: receipt.status === 1,
        txHash: tx.hash,
        levels: uniqueSortedLevels,
        gasUsed: receipt.gasUsed ? receipt.gasUsed.toString() : undefined,
      };
    } catch (err) {
      let userMessage = 'Multi-level purchase failed.';
      const errMsg = err?.message?.toLowerCase() || '';
      if (errMsg.includes('insufficient funds') || errMsg.includes('insufficient usdt')) {
        userMessage = 'Insufficient balance. Please check your USDT balance.';
      } else if (errMsg.includes('allowance') || errMsg.includes('approval')) {
        userMessage = 'Insufficient USDT allowance. Please approve the contract.';
      } else if (errMsg.includes('already own')) {
        userMessage = 'You already own one or more selected levels.';
      } else if (errMsg.includes('need level') || errMsg.includes('previous level')) {
        userMessage = 'You must purchase previous levels first.';
      } else if (errMsg.includes('paused')) {
        userMessage = 'Contract is paused. Please try again later.';
      } else if (errMsg.includes('execution reverted')) {
        userMessage = 'Transaction reverted. Please check requirements.';
      } else if (errMsg.includes('rejected') || err?.code === 4001) {
        userMessage = 'Transaction cancelled by user.';
      } else if (err?.errorName === 'Error' && err?.data) {
        try {
          const reason = ethers.utils.toUtf8String(err.data.slice(138));
          userMessage = `Contract reverted: ${reason}`;
        } catch {}
      } else if (err?.reason) {
        userMessage = `Contract reverted: ${err.reason}`;
      } else if (err?.data && typeof err.data === 'string') {
        try {
          const reason = ethers.utils.toUtf8String(err.data.slice(138));
          userMessage = `Contract reverted: ${reason}`;
        } catch {}
      }
      setActionError(userMessage);
      throw new Error(userMessage);
    } finally {
      setTxLoading(false);
    }
  };
  
  // ============ MINE TOKENS ============
  const mineTokens = async (usdtAmount) => {
    if (!account || !signer) throw new Error('Please connect your wallet');
    
    setTxLoading(true);
    setActionError(null);
    setSuccessMessage(null);
    
    try {
      const amountUSDT = parseUSDT(usdtAmount);
      console.log(`[Mine] Mining amount:`, usdtAmount, 'USDT');
      
      const dolphinWithSigner = new ethers.Contract(
        CONTRACTS.DOLPHIN,
        [
          'function mineTokens(uint256 usdtAmount)'
        ],
        signer
      );
      
      const usdtWithSigner = new ethers.Contract(
        CONTRACTS.USDT,
        [
          'function balanceOf(address) view returns (uint256)',
          'function approve(address,uint256) returns (bool)',
          'function allowance(address,address) view returns (uint256)',
          'function decimals() view returns (uint8)'
        ],
        signer
      );
      
      const decimals = await usdtWithSigner.decimals();
      
      // Check balance
      const balance = await usdtWithSigner.balanceOf(account);
      console.log(`[Mine] Balance:`, ethers.utils.formatUnits(balance, decimals), 'USDT');
      
      if (balance.lt(amountUSDT)) {
        throw new Error(`Insufficient USDT. Need ${usdtAmount} USDT, have ${ethers.utils.formatUnits(balance, decimals)} USDT`);
      }
      
      // Check allowance
      const allowance = await usdtWithSigner.allowance(account, CONTRACTS.DOLPHIN);
      console.log(`[Mine] Allowance:`, ethers.utils.formatUnits(allowance, decimals), 'USDT');
      
      if (allowance.lt(amountUSDT)) {
        console.log(`[Mine] Approving USDT...`);
        const approveAmount = amountUSDT.mul(2);
        const approveTx = await usdtWithSigner.approve(CONTRACTS.DOLPHIN, approveAmount);
        console.log(`[Mine] Approval TX:`, approveTx.hash);
        
        await approveTx.wait();
        await new Promise(resolve => setTimeout(resolve, 3000));
        console.log(`[Mine] Approval complete`);
      }
      
      // Estimate gas
      console.log(`[Mine] Estimating gas...`);
      let gasLimit;
      try {
        const estimatedGas = await dolphinWithSigner.estimateGas.mineTokens(amountUSDT);
        console.log(`[Mine] Estimated gas:`, estimatedGas.toString());
        gasLimit = estimatedGas.mul(125).div(100);
        console.log(`[Mine] Using gas limit:`, gasLimit.toString());
      } catch {
        gasLimit = 2000000;
      }
      
      // Mine tokens
      console.log(`[Mine] Mining tokens...`);
      const tx = await dolphinWithSigner.mineTokens(amountUSDT, { gasLimit, value: 0 });
      
      console.log(`[Mine] Transaction sent:`, tx.hash);
      setSuccessMessage('Mining transaction sent...');
      
      const receipt = await tx.wait();
      console.log(`[Mine] Transaction confirmed in block ${receipt.blockNumber}`);
      
      if (receipt.status === 1) {
        console.log(`[Mine] Mining successful!`);
        
        await Promise.all([
          fetchUserStats(),
          fetchStats(),
          fetchTransactionHistory()
        ]);
        setSuccessMessage('✅ Successfully mined tokens!');
        
        return {
          success: true,
          txHash: tx.hash,
          amount: usdtAmount,
          gasUsed: receipt.gasUsed.toString(),
        };
      } else {
        throw new Error('Transaction failed');
      }
      
    } catch (err) {
      console.error(`[mineTokens] Error:`, err);
      
      let userMessage = 'Transaction failed';
      if (err.code === 4001) userMessage = 'Transaction rejected by user';
      if (err.message?.includes('insufficient funds')) userMessage = 'Insufficient MATIC for gas';
      if (err.message?.includes('Insufficient USDT')) userMessage = 'Insufficient USDT balance';
      
      setActionError(userMessage);
      throw new Error(userMessage);
      
    } finally {
      setTxLoading(false);
    }
  };

  // ============ CLAIM TOKENS ============
  const claimTokens = async () => {
    if (!account || !signer) throw new Error('Please connect your wallet');
    
    setTxLoading(true);
    setActionError(null);
    setSuccessMessage(null);
    
    try {
      const dolphinWithSigner = new ethers.Contract(
        CONTRACTS.DOLPHIN,
        [
          'function claimTokens()',
          'function estimateGas.claimTokens() returns (uint256)'
        ],
        signer
      );
      
      // Estimate gas
      let gasLimit;
      try {
        const estimatedGas = await dolphinWithSigner.estimateGas.claimTokens();
        gasLimit = estimatedGas.mul(125).div(100);
      } catch {
        gasLimit = 300000;
      }
      
      console.log('[Claim] Claiming tokens...');
      const tx = await dolphinWithSigner.claimTokens({ gasLimit });
      
      console.log('[Claim] TX sent:', tx.hash);
      setSuccessMessage('Claiming tokens...');
      
      const receipt = await tx.wait();
      
      if (receipt.status === 1) {
        console.log('[Claim] Success!');
        await Promise.all([
          fetchUserStats(),
          fetchTransactionHistory()
        ]);
        setSuccessMessage('✅ Tokens claimed successfully!');
        
        return {
          success: true,
          txHash: tx.hash,
          gasUsed: receipt.gasUsed.toString(),
        };
      } else {
        throw new Error('Claim failed');
      }
      
    } catch (err) {
      console.error('[claimTokens] Error:', err);
      
      let userMessage = 'Claim failed';
      if (err.code === 4001) userMessage = 'Transaction rejected';
      if (err.message?.includes('Nothing to claim')) userMessage = 'Nothing to claim';
      
      setActionError(userMessage);
      throw err;
    } finally {
      setTxLoading(false);
    }
  };

  // ============ CALCULATE REFERRAL EARNINGS ============
  const calculateReferralEarnings = useCallback((level) => {
    const levelPrices = {
      1: 3, 2: 100, 3: 200, 4: 400, 5: 800,
      6: 1600, 7: 3200, 8: 6400, 9: 12800,
      10: 25600, 11: 51200, 12: 102400
    };
    
    const price = levelPrices[level] || 0;
    const mlmPortion = price * 0.5;
    const yourShare = mlmPortion * 0.4;
    
    return {
      level,
      price,
      mlmPortion,
      yourShare,
      formatted: `${yourShare.toFixed(2)} USDT`
    };
  }, []);

  // ============ GET DOWNLINE EARNINGS SUMMARY ============
  const getDownlineEarningsSummary = useCallback(async () => {
    if (!dolphinContract || !account) return null;
    try {
      // Use getUserInfo to fetch referral earnings
      const userInfo = await safeViewCall('getUserInfo', account);
      // getUserInfo returns a tuple, referralEarnings is the 7th value (index 6)
      const referralEarnings = userInfo && userInfo.length >= 7 ? userInfo[6] : 0;
      return {
        totalEarned: formatUSDT(referralEarnings || 0),
        fromLevels: {},
        lastEarningDate: null,
        estimatedMonthly: '0'
      };
    } catch (error) {
      console.log('[getDownlineEarningsSummary] Error:', error);
      return null;
    }
  }, [dolphinContract, account, formatUSDT, safeViewCall]);

  // ============ GET LEVEL PRICE ============
  const getLevelPrice = useCallback(async (level) => {
    if (!dolphinContract) return '0';
    const price = await safeViewCall('getLevelPriceUSDT', level);
    return formatUSDT(price || 0);
  }, [dolphinContract, formatUSDT, safeViewCall]);

  // ============ REFRESH ALL DATA ============
  const refreshAllData = async () => {
    console.log('[refreshAllData] Manual refresh triggered');
    await Promise.all([
      fetchStats(),
      fetchUserStats(),
      fetchReferralStats(),
      fetchTransactionHistory(),
      fetchFullDownlineTree(2)
    ]);
  };

  // ============ CLEAR MESSAGES ============
  const clearMessages = useCallback(() => {
    setActionError(null);
    setSuccessMessage(null);
  }, []);

  // ============ CLEAR PENDING REFERRAL ============
  const clearPendingReferral = useCallback(() => {
    setPendingReferral(null);
    localStorage.removeItem('pendingReferral');
  }, []);

  // ============ INITIALIZE LEVELS ============
  useEffect(() => {
    const initLevels = async () => {
      if (!dolphinContract) return;
      
      const prices = [];
      for (let i = 1; i <= 12; i++) {
        const price = await getLevelPrice(i);
        prices.push(price);
      }
      
      const levelData = prices.map((price, index) => ({
        id: index + 1,
        price: price,
        name: `Level ${index + 1}`,
        description: `Unlocks Level ${index + 1} features and rewards`,
        isPurchased: userStats.levels.includes(index + 1) || userStats.isSpecial,
      }));
      
      setLevels(levelData);
    };
    
    if (dolphinContract) {
      initLevels();
    }
  }, [dolphinContract, userStats.levels, userStats.isSpecial, getLevelPrice]);

  // ============ DEBUG: Log special addresses to console when contract is ready ============
  useEffect(() => {
    if (!dolphinContract) return;
    (async () => {
      try {
        const addresses = await dolphinContract.getSpecialAddresses();
        console.log('[Special Addresses]', addresses);
      } catch (err) {
        console.error('[Special Addresses] Error:', err);
      }
    })();
  }, [dolphinContract]);

  // ============ AUTO REFRESH ============
  useEffect(() => {
    if (!account || !dolphinContract) return;
    
    console.log('[useDolphin] Setting up automatic refresh...');
    
    // Clear existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    
    // Refresh every 30 seconds
    refreshIntervalRef.current = setInterval(() => {
      if (account && dolphinContract) {
        Promise.all([
          fetchUserStats(),
          fetchReferralStats()
        ]).catch(err => console.log('[Auto-refresh] Error:', err.message));
      }
    }, 30000);
    
    // Initial load
    Promise.all([
      fetchStats(),
      fetchUserStats(),
      fetchReferralStats(),
      fetchTransactionHistory(),
      fetchFullDownlineTree(1)
    ]).catch(err => console.log('[Initial Load] Error:', err.message));
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [account, dolphinContract, fetchStats, fetchUserStats, fetchReferralStats, fetchTransactionHistory, fetchFullDownlineTree]);

  // Fast preload of all critical data on startup or user connect
  useEffect(() => {
    async function preloadAll() {
      if (!dolphinContract || !account) return;
      setLoading(true);
      try {
        // Fetch all level prices (assuming 12 levels)
        const levelPromises = [];
        for (let i = 1; i <= 12; i++) {
          levelPromises.push(dolphinContract.getLevelPriceUSDT(i));
        }
        const levelPrices = await Promise.all(levelPromises);
        const levelData = levelPrices.map((price, index) => ({
          id: index + 1,
          price: price.toString(),
          name: `Level ${index + 1}`,
          description: `Unlocks Level ${index + 1} features and rewards`,
          isPurchased: false,
        }));
        setLevels(levelData);

        // Fetch user stats, downlines, and history in parallel
        let downlines = [];
        // Debug: print the account and contract address used
        if (dolphinReferralContract && account) {
          console.log('[preloadAll] Calling getDownlinesByRank with account:', account, 'on contract:', dolphinReferralContract.address);
        }
        const [, downlinesResult, history] = await Promise.all([
          fetchUserStats(),
          dolphinReferralContract ? dolphinReferralContract.getDownlinesByRank(account, 5) : Promise.resolve([]),
          fetchTransactionHistory(50)
        ]); // Remove unused userStatsData
        downlines = downlinesResult;
        if (dolphinReferralContract) {
          console.log('getDownlinesByRank result:', downlines, 'for account:', account, 'on contract:', dolphinReferralContract.address);
        }
        // setUserStats(userStatsData); // Do not set userStats here; fetchUserStats updates state internally
        setDownlineTree({ loading: false, error: null, data: downlines, lastUpdated: Date.now() });
        setTransactionHistory(history);
      } catch (err) {
        console.error('[preloadAll] Error:', err);
      }
      setLoading(false);
    }
    preloadAll();
    // Only run when contracts or account change
  // eslint-disable-next react-hooks/exhaustive-deps
  }, [dolphinContract, dolphinReferralContract, account, fetchTransactionHistory, fetchUserStats]); // Add missing dependencies

  return {
    // Contracts
    dolphinContract,
    usdtContract,
    dolphinReferralContract,
    dolphinPaymentsContract,
    dolphinVestingContract,

    // State
    stats,
    userStats,
    referralStats,
    levels,
    loading,
    txLoading,
    actionError,
    successMessage,
    inviteCode,
    pendingReferral,
    downlineTree,
    transactionHistory,
    historyLoading,

    // Actions
    purchaseLevel,
    purchaseLevels,
    mineTokens,
    claimTokens,
    generateInviteCode,
    setUserReferrer,
    refreshAllData,
    clearMessages,
    clearPendingReferral,

    // Downline functions
    fetchFullDownlineTree,
    getDownlineDetails,
    getDownlineEarningsSummary,

    // Data fetching
    fetchStats,
    fetchUserStats,
    fetchReferralStats,
    fetchTransactionHistory,
    
    // Special address helpers
    isSpecialAddress,
    isCurrentUserSpecial,

    // Referral helpers
    getMyReferralLink,
    shortenAddress,
    formatInviteCode,
    calculateReferralEarnings,

    // Price helpers
    getLevelPrice,
    formatUSDT,
    formatTokens,
    parseUSDT,
    getTotalSpentUSDT,

    // Theme colors
    themeColors,
  };
};

export default useDolphin;